import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Select, Space, Spin, Table, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { _debounce } from '@medicusunion/front-utils';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { fetchPatients } from 'services/api/users';
import { fetchBillingAddresses } from 'services/api/invoicingBillingAddresses';
import { PatientListRow } from 'types/api/users';
import { BillingAddress } from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import AddressModal from './components/AddressModal';

const debounce = _debounce();
const PATIENT_LIMIT = 20;

type PatientOption = { label: string; value: number };

const BillingAddresses: FC = () => {
  const [patientQuery, setPatientQuery] = useState('');
  const [patientOptions, setPatientOptions] = useState<PatientListRow[]>([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patient, setPatient] = useState<PatientOption | undefined>();

  const [addresses, setAddresses] = useState<BillingAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BillingAddress | null>(null);

  const patientId = patient?.value ?? null;
  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  // Patient autocomplete — spans all organizations (org header nulled).
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setPatientLoading(true);
        const res = await fetchPatients(
          {
            page: 1,
            limit: PATIENT_LIMIT,
            search: patientQuery
          },
          { headers: { organization: null as unknown as string } }
        );

        if (!cancelled) setPatientOptions(res.results);
      } catch {
        // swallow — empty list on failure
      } finally {
        if (!cancelled) setPatientLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [patientQuery]);

  // Billing addresses for the selected patient.
  useEffect(() => {
    if (patientId == null) {
      setAddresses([]);

      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const rows = await fetchBillingAddresses(patientId);
        if (!cancelled) setAddresses(rows);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [patientId, refreshKey]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((address: BillingAddress) => {
    setEditing(address);
    setModalOpen(true);
  }, []);

  const patientSelectOptions: PatientOption[] = patientOptions.map(item => ({
    label: `${item.first_name} ${item.last_name} (${item.email})`,
    value: item.id
  }));

  const columns: ColumnProps<BillingAddress>[] = useMemo(
    () => [
      { title: 'Full name', dataIndex: 'fullName', key: 'fullName' },
      {
        title: 'Company',
        dataIndex: 'company',
        key: 'company',
        render: (value?: string | null) => value || '—'
      },
      {
        title: 'VAT ID',
        dataIndex: 'vatId',
        key: 'vatId',
        render: (value?: string | null) => value || '—'
      },
      {
        title: 'Address',
        key: 'address',
        render: (_, row) =>
          [row.line1, row.line2, row.postalCode, row.city, row.country]
            .filter(Boolean)
            .join(', ')
      },
      {
        title: 'Default',
        dataIndex: 'isDefault',
        key: 'isDefault',
        render: (isDefault: boolean) =>
          isDefault ? <Tag color="blue">Default</Tag> : null
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string) => dayjs(value).format(FULL_DATE_FORMAT)
      },
      {
        title: '',
        key: 'actions',
        align: 'right',
        render: (_, row) => (
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
        )
      }
    ],
    [openEdit]
  );

  return (
    <PageWrapper
      title="Billing addresses"
      color="white"
      extra={[
        <Button
          key="add"
          type="primary"
          icon={<PlusOutlined />}
          disabled={patientId == null}
          onClick={openCreate}
        >
          Add address
        </Button>
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Select
          showSearch
          labelInValue
          filterOption={false}
          placeholder="Search a patient by name or email"
          style={{ width: '100%', maxWidth: 480 }}
          options={patientSelectOptions}
          loading={patientLoading}
          value={patient}
          onChange={option => setPatient(option as PatientOption)}
          onSearch={input => debounce(() => setPatientQuery(input))}
          notFoundContent={
            patientLoading ? (
              <Spin size="small" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        />
        {patientId != null && (
          <Table
            dataSource={addresses}
            columns={columns}
            loading={loading}
            rowKey="publicId"
            pagination={false}
          />
        )}
      </Space>
      {patientId != null && (
        <AddressModal
          open={modalOpen}
          patientId={patientId}
          address={editing}
          onClose={() => setModalOpen(false)}
          onSuccess={reload}
        />
      )}
    </PageWrapper>
  );
};

export default BillingAddresses;
